import { AlertTriangle } from 'lucide-react';
import { ResourceChip } from '@reflex/ui/components/resource-preview/ResourceChip';
import { AgentTypeChip } from '@reflex/ui/components/agent/AgentTypeChip';
import { ModelChip } from '@reflex/ui/components/agent/ModelChip';
import { modelProviderSecretDisplayName } from '@reflex/ui/components/model-providers/secret-display';
import { getAgentHarnessIconConfig } from '@reflex/ui/lib/model-icons';
import {
  useAccessibleModelProviderSecrets,
  useAgentProviderKeyTarget,
} from '@reflex/ui/client/hooks';
import { useAgentPersonas } from '@reflex/plugin-agent-persona/web/useAgentPersonas';
import { DEFAULT_MODELS } from '@reflex/plugin-api/models';
import { resolveModelId } from '@reflex/plugin-api/providers/internals';
import { useFlowRuntimeAgentType } from '../useFlowRuntimeAgentType.js';
import { cn } from '@reflex/ui/lib/utils';

/**
 * Runtime fallback the flow engine applies to an `llm`/`router` node when
 * neither the node nor its persona pins a model. Mirrors `llm-adapter.ts` so
 * the chip on the canvas always shows what the node will actually run with.
 * The agent-type chain is resolved by `useFlowRuntimeAgentType`, which
 * follows the pinned model into the agent type that offers it.
 */
const RUNTIME_DEFAULT_MODEL = resolveModelId(DEFAULT_MODELS.flowLlm);

/**
 * Which fallback-able dimensions a node type supports. When a slot is
 * enabled the chip is always rendered with the effective value (node pin →
 * persona → runtime default). `personaId` is intentionally not a slot — a
 * persona is an explicit, optional identity (there is no "default
 * persona"), so it only renders when set.
 */
interface NodeChipSlots {
  agentType?: boolean;
  model?: boolean;
  providerKey?: boolean;
}

interface FlowNodeResourceChipsProps {
  personaId?: string;
  agentType?: string;
  model?: string;
  providerSecretId?: string;
  slots?: NodeChipSlots;
}

/**
 * Warning chip shown when no provider key resolves for this node (nothing
 * pinned, no persona key). Mirrors the missing-key warning in the agent
 * launch dialog so the flow surfaces the problem before run instead of
 * failing silently at execution time.
 */
function MissingKeyChip() {
  return (
    <span
      title="No provider key resolved. Pin one in node settings or the automation will fail at runtime."
      className="inline-flex w-fit shrink-0 items-center justify-center gap-1 self-start rounded bg-[#fbf3e3] p-1 text-xs font-medium leading-3 text-[#b56b0e]"
      data-testid="flow-node-missing-key-warning"
    >
      <AlertTriangle className="size-3" aria-hidden="true" />
      Add key
    </span>
  );
}

/**
 * Renders the small standardized chips (with hover previews) for the
 * agent/model/provider-secret selections a flow node carries. Wrapped in a
 * `nodrag nopan` container so interacting with a chip on the canvas opens its
 * preview instead of dragging the node.
 */
export function FlowNodeResourceChips({
  personaId,
  agentType,
  model,
  providerSecretId,
  slots,
}: FlowNodeResourceChipsProps) {
  const { data: personas } = useAgentPersonas();
  const persona =
    personaId && Array.isArray(personas) ? personas.find((p) => p.id === personaId) : undefined;

  // Resolve what the node will actually run with: node pin → persona →
  // runtime default. Mirrors the chain in `agent-adapter.ts` /
  // `llm-adapter.ts` so the canvas never shows a stale "Default" placeholder.
  const effectiveModel = model ?? persona?.model ?? RUNTIME_DEFAULT_MODEL;
  const effectiveAgentType = useFlowRuntimeAgentType(
    agentType ?? persona?.agentType,
    model ?? persona?.model,
  );
  const effectiveSecretId = providerSecretId ?? persona?.modelProviderSecretId ?? undefined;

  const { secrets } = useAccessibleModelProviderSecrets({
    enabled: Boolean(effectiveSecretId),
  });
  const secret = effectiveSecretId ? secrets.find((s) => s.id === effectiveSecretId) : undefined;

  const showAgentType = Boolean(slots?.agentType) || Boolean(agentType);
  const showModel = Boolean(slots?.model) || Boolean(model);
  const showProviderKey = Boolean(slots?.providerKey) || Boolean(providerSecretId);

  // The shared resolver makes service-managed and free-tier providers keyless.
  // Wait for its matrix before showing an error: a fallback while it loads is
  // deliberately conservative and would otherwise flash a false warning. If
  // it fails, use that conservative fallback instead of hiding a real error.
  const { keyTypes, matrixLoaded, matrixError } = useAgentProviderKeyTarget(
    effectiveAgentType,
    effectiveModel,
  );
  const keyRequired = (matrixLoaded || matrixError) && keyTypes.length > 0;

  if (!personaId && !showAgentType && !showModel && !showProviderKey) return null;

  const missingKey = showProviderKey && !effectiveSecretId && keyRequired;
  const hasLeading =
    Boolean(personaId) ||
    showAgentType ||
    showModel ||
    Boolean(showProviderKey && effectiveSecretId);

  return (
    <div
      className={cn(
        'nodrag nopan flex w-full items-center gap-2',
        hasLeading && missingKey && 'justify-between',
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {personaId && <ResourceChip kind="personas" id={personaId} name={persona?.name} />}
        {showAgentType && (
          <AgentTypeChip
            agentType={effectiveAgentType}
            icon={getAgentHarnessIconConfig(effectiveAgentType)?.icon}
          />
        )}
        {showModel && <ModelChip model={effectiveModel} />}
        {showProviderKey && effectiveSecretId ? (
          <ResourceChip
            kind="cost-keys"
            id={effectiveSecretId}
            name={secret ? modelProviderSecretDisplayName(secret) : undefined}
          />
        ) : null}
      </div>
      {missingKey ? <MissingKeyChip /> : null}
    </div>
  );
}
